import { Card, CardActions, CardContent, CardHeader, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import axios from 'axios';
import { FormContainer, PasswordElement, PasswordRepeatElement } from 'react-hook-form-mui';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { requestLogout } from '../../../asyncHelpers/authService';
import useAsyncModifyPassword from '../../../hooks/useAsyncModifyPassword';
import { RequestStatus } from '../../../sic-lib/enums/RequestStatus';
import { buildNewPassword } from '../../../sic-lib/types/Password';
import { useAppDispatch } from '../../../store/hooks';
import { createToast } from '../../../store/notify';
import { clearAuth, updateUserPassword } from '../../../store/users';
import { createErrorToast } from '../../../utils/notifyHelpers';
import ButtonLoading from '../../Shared/ButtonLoading';

interface FormState {
    oldPassword: string;
    password: string;
}

interface Props {}

const ChangePassword = ({}: Props) => {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();
    const { asyncModifyPassword, requestStatus } = useAsyncModifyPassword();

    const request_token = axios.CancelToken.source();
    const history = useHistory();

    const handleLogout = async () => {
        try {
            await requestLogout(request_token.token);
            dispatch(clearAuth());
        } catch {
            console.log('error logout');
        }
        setTimeout(() => history.push('/login'), 5000);
        document.location.reload();
    };

    const saveNewPassword = async (formState: FormState) => {
        const updatedPassword = buildNewPassword({
            oldPassword: formState.oldPassword,
            password: formState.password,
        });

        const asyncUpdatedPassword = await asyncModifyPassword(updatedPassword);

        if (asyncUpdatedPassword) {
            dispatch(updateUserPassword(asyncUpdatedPassword));
            const toastData = createErrorToast('Password Changed');
            dispatch(createToast({ toastData }));
            handleLogout();
        }
    };

    return (
        <FormContainer<FormState> onSuccess={saveNewPassword}>
            <Grid container justifyContent="center">
                <Grid item xs={6}>
                    <Card>
                        <CardHeader title="Change Password" />
                        <Divider />
                        <CardContent>
                            <Grid container direction="column" spacing={2}>
                                <Grid item>
                                    <PasswordElement
                                        name="oldPassword"
                                        label="Old Password"
                                        type="password"
                                        required
                                        validation={{
                                            minLength: 6,
                                            maxLength: 250,
                                            pattern: /^(?=.*\d).{6,}$/,
                                        }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item>
                                    <PasswordElement
                                        name="password"
                                        label="New Password"
                                        type="password"
                                        required
                                        validation={{
                                            minLength: 6,
                                            maxLength: 250,
                                            pattern: /^(?=.*\d).{6,}$/,
                                        }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item>
                                    <PasswordRepeatElement
                                        passwordFieldName="password"
                                        name="password-repeat"
                                        label="Repeat Password"
                                        fullWidth
                                        required
                                        validation={{
                                            maxLength: 250,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                        <Divider />
                        <CardActions>
                            <ButtonLoading
                                isLoading={requestStatus === RequestStatus.active}
                                color="primary"
                                variant="contained"
                                type="submit"
                            >
                                Save New Password
                            </ButtonLoading>
                        </CardActions>
                    </Card>
                </Grid>
            </Grid>
        </FormContainer>
    );
};

export default ChangePassword;

const useStyles = makeStyles()((theme: Theme) => ({
    paper: { padding: '2rem' },
    header: { margin: '1rem 0' },
    form: { margin: '1rem 0' },
    btnCancel: {
        color: theme.palette.btnDefault.main,
        '&:hover': {
            backgroundColor: theme.palette.btnDefault.light,
        },
    },
    btnGroup: {
        margin: '1rem 0',
    },
}));
